import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { 
  FileText, Plus, Trash2, Download, Clock, 
  TrendingUp, Loader2, MoreVertical, BarChart3, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { useAuth } from '../context/AuthContext';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

export default function ProspectusListPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, viewAsOrg, user } = useAuth();
  const [prospectuses, setProspectuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  // Default the org filter to the currently scoped tenant. For
  // a super admin previewing an org (orange "Previewing as X"
  // banner), that's `viewAsOrg.id`. For everyone else it's their
  // home org. Falling back to "all" was misleading because the
  // grid would surface cases from tenants the user wasn't even
  // looking at.
  const scopedOrgId =
    (viewAsOrg && viewAsOrg.id) || user?.organization_id || 'all';
  const [selectedOrg, setSelectedOrg] = useState(scopedOrgId);

  // Re-sync the filter when the global tenant context changes
  // (super admin switches preview org from the header).
  useEffect(() => {
    setSelectedOrg(scopedOrgId);
  }, [scopedOrgId]);

  const fetchProspectuses = useCallback(async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const orgParam = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const response = await api.get(`/prospectus${orgParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProspectuses(response.data);
    } catch (err) {
      logger.error('Error fetching prospectuses:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    fetchProspectuses();
  }, [fetchProspectuses]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const token = secureStorage.getItem('session_token');
    api.get(`/rbac/organizations`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setOrganizations(res.data.organizations || [])).catch(() => {});
  }, [isSuperAdmin]);

  const deleteProspectus = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const token = secureStorage.getItem('session_token');
      await api.delete(`/prospectus/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProspectuses(prev => prev.filter(p => p.prospectus_id !== deleteId));
    } catch (err) {
      logger.error('Error deleting:', err);
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const exportProspectus = async (id, format) => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.post(
        `/prospectus/${id}/export/${format}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const prospectus = prospectuses.find(p => p.prospectus_id === id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = { pdf: 'pdf', pptx: 'pptx', docx: 'docx' };
      link.setAttribute('download', `${prospectus?.project_name || 'Prospectus'}.${ext[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      logger.error('Export error:', err);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const symbols = { USD: '$', EUR: '€', GBP: '£' };
    const symbol = symbols[currency] || '$';
    if (amount >= 1000000) return `${symbol}${(amount/1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${symbol}${(amount/1000).toFixed(0)}K`;
    return `${symbol}${amount?.toLocaleString() || 0}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  const prospectusHeroActions = (
    <>
      {isSuperAdmin && organizations.length > 0 && (
        <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}
          className="text-sm rounded-lg px-3 py-1.5 bg-white border border-slate-200 text-slate-800 border-0 focus:ring-2 focus:ring-white/50 min-w-[160px]"
          data-testid="prospectus-org-filter">
          <option value="all">All Organizations</option>
          {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
      {prospectuses.length >= 2 && (
        <Button
          variant="outline"
          onClick={() => {
            setSelectionMode(!selectionMode);
            if (selectionMode) setSelectedIds([]);
          }}
          className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          data-testid="compare-mode-btn"
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          {selectionMode ? 'Cancel Compare' : 'Compare'}
        </Button>
      )}
      <Button onClick={() => navigate('/prospectus/new')} className={__pageTheme.actionBtn} data-testid="new-prospectus-btn">
        <Plus className="w-4 h-4 mr-2" />
        New Business Case
      </Button>
    </>
  );
  const prospectusHeroStats = [
    { label: 'Total Cases', value: prospectuses.length, testId: 'prospectus-stat-total' },
    { label: 'Draft', value: prospectuses.filter(p => p.status === 'draft').length, testId: 'prospectus-stat-draft' },
    { label: 'Completed', value: prospectuses.filter(p => p.status === 'completed').length, testId: 'prospectus-stat-completed' },
  ];

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Custom Business Case"
        description="Create standalone business cases for stakeholders. Build custom proposals without solution data, compare scenarios, and export professional documents."
        theme={__pageTheme}
        icon={Briefcase}
        actions={prospectusHeroActions}
        stats={prospectusHeroStats}
        testId="prospectus-list-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6 animate-fade-in">

        {prospectuses.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No business cases yet</h3>
              <p className="text-gray-500 mb-6">
                Create your first business case to present to stakeholders
              </p>
              <Button onClick={() => navigate('/prospectus/new')}>
                <Plus className="w-4 h-4 mr-2" />
                Create Prospectus
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {prospectuses.map(prospectus => (
              <Card 
                key={prospectus.prospectus_id} 
                className={`hover:shadow-lg transition-all cursor-pointer ${
                  selectionMode && selectedIds.includes(prospectus.prospectus_id) 
                    ? 'ring-2 ring-blue-500 bg-blue-50' 
                    : ''
                }`}
                data-testid={`prospectus-card-${prospectus.prospectus_id}`}
                onClick={() => {
                  if (selectionMode) {
                    setSelectedIds(prev => 
                      prev.includes(prospectus.prospectus_id)
                        ? prev.filter(id => id !== prospectus.prospectus_id)
                        : prev.length < 4 ? [...prev, prospectus.prospectus_id] : prev
                    );
                  }
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3 flex-1 min-w-0 pr-2">
                      {selectionMode && (
                        <Checkbox 
                          checked={selectedIds.includes(prospectus.prospectus_id)}
                          className="mt-1"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg truncate">
                          {prospectus.project_name || 'Untitled'}
                        </CardTitle>
                        <CardDescription className="truncate">
                          {prospectus.organization_name || 'No organization'}
                        </CardDescription>
                      </div>
                    </div>
                    {!selectionMode && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/prospectus/${prospectus.prospectus_id}`)}>
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => exportProspectus(prospectus.prospectus_id, 'pdf')}>
                            Export PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => exportProspectus(prospectus.prospectus_id, 'pptx')}>
                            Export PowerPoint
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => exportProspectus(prospectus.prospectus_id, 'docx')}>
                            Export Word
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => setDeleteId(prospectus.prospectus_id)}
                            className="text-rose-600"
                          >
                            Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent onClick={() => !selectionMode && navigate(`/prospectus/${prospectus.prospectus_id}`)}>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Investment</p>
                      <p className="font-semibold">
                        {formatCurrency(prospectus.total_investment, prospectus.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">ROI</p>
                      <p className="font-semibold text-emerald-600">
                        {((prospectus.roi_projection || 0) / 100).toFixed(2)}x
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center pt-3 border-t">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(prospectus.updated_at || prospectus.created_at)}
                    </div>
                    <Badge variant={
                      prospectus.confidence_level === 'high' ? 'default' :
                      prospectus.confidence_level === 'medium' ? 'secondary' : 'outline'
                    }>
                      {prospectus.confidence_level || 'draft'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {/* Selection Mode Action Bar */}
      {selectionMode && selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-full px-6 py-3 flex items-center gap-4 border z-50">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedIds([])}
          >
            Clear
          </Button>
          <Button 
            size="sm"
            onClick={() => navigate(`/prospectus/compare?ids=${selectedIds.join(',')}`)}
            disabled={selectedIds.length < 2}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Compare Selected
          </Button>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prospectus?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The prospectus and all its data will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteProspectus} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </PageShell>
  );
}
